import { styled } from '@material-ui/core'
import Portal from '@material-ui/core/Portal'
import { orgSelectors } from 'ducks/orgs'
import { HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2 } from 'elements/hardwareFilter/constantsV2'
import { DISTRIBUTOR_FILTER_KEY_V2 } from 'elements/hardwareFilter/distributor/constants'
import { GLOBAL_SUPPLIER_NODE_KEY_V2 } from 'elements/hardwareFilter/distributor/GlobalDistributorFilterFactory'
import { FEATURE_CONFIG_BRAND_FILTER_NODE_KEY_V2 } from 'elements/hardwareFilter/manufacturerV2/constants'
import { SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2 } from 'elements/hardwareFilter/systemCompatibility/SystemCompatibilityFilterNodeFactoryV2'
import { VIEW_FROM_FILTER_NODE_KEY_V2 } from 'elements/hardwareFilter/viewFrom/ViewFromFilterNodeFactoryV2'
import { DistributorTradePricingContext } from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import listQueryParamsFormatter from 'elements/input/listQueryParamsFormatter'
import inflection from 'inflection'
import { ComponentVersionsInherit, ComponentVersions_3_0, Grid, Typography, useTheme } from 'opensolar-ui'
import { useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import type { ComponentTypesV2 } from 'types/selectComponent'
import HardwareSelectorFilterSidebar from '../../../elements/hardwareSelectorV2/elements/HardwareSelectorFilterSidebar'
import useHardwareSelectorFetchEngine from '../../../elements/hardwareSelectorV2/fetch/useHardwareSelectorFetchEngine'
import { HARDWARE_FILTER_SIDEBAR_ID } from '../catalog/HardwareFilterSidebar'
import { useShopCategoryConfig } from '../categoryConfig'
import { majorComponentCategories, otherCategories } from '../configs'
import NoResultFoundPage from '../elements/NoResultFoundPage'
import ShopPageContainer from '../elements/ShopPageContainer'
import ProductGrid from '../home/ProductGrid'
import useEnabledHardwareSuppliers from '../hooks/useEnabledHardwareSuppliers'
import { useGetSupplierConfig } from '../hooks/useGetSupplierConfig'
import { HardwareSupplierFilterKeyType } from '../type'
import ShopPagination from './ShopPagination'

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const NoResults = ({ text }: { text: string }) => {
  const theme = useTheme()
  const translate = useTranslate()
  return (
    <Grid container direction="column">
      <Grid item>
        <Typography textVariant="h5Bold">{translate('Results for "%{searchText}"', { searchText: text })}</Typography>
        <Typography textVariant="body1" colorHex={theme.palette.neutralGrey[1000]}>
          0 {translate('results')}
        </Typography>
      </Grid>
    </Grid>
  )
}

const othersKeyWords = ['other', 'others']

export const ShopItemsWithFilter = ({
  trackSource,
  searchText,
  componentType,
  distributor,
  initialComponentsData,
  initialTitle,
  otherSubtypes,
  categoryName,
}: {
  trackSource?: string
  searchText?: string
  componentType?: ComponentTypesV2
  distributor?: HardwareSupplierFilterKeyType
  initialComponentsData?: any[]
  initialTitle?: string
  otherSubtypes?: string[]
  categoryName?: string
}) => {
  const [localPage, setLocalPage] = useState(1)
  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()
  const { getCategoryLabel } = useShopCategoryConfig()

  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId: useSelector(orgSelectors.getOrg)?.id || 0,
    distributors: hardwareDistributors,
  })
  const translate = useTranslate()
  const theme = useTheme()
  const { getSupplierConfig } = useGetSupplierConfig()
  const supplierConfig = distributor ? getSupplierConfig(distributor) : null
  const supplierName = supplierConfig?.name || ''

  const componentTypeLabel = useMemo(() => {
    if (categoryName) return categoryName
    if (!componentType) return 'Products'
    const configLabel = getCategoryLabel(componentType)
    if (configLabel) return configLabel
    return inflection.capitalize(inflection.pluralize(componentType))
  }, [getCategoryLabel, categoryName, componentType])

  const hardware_config_sidebar = useMemo(() => {
    // FEATURE_CONFIG_BRAND_FILTER_NODE_KEY_V2 is the static `manufacturer_filter` public
    // feature config. In the Shop the dynamic DistributorBrand filter (driven by
    // `manufacturer_list`) is the source of truth for brands, so we suppress this entry
    // from the sidebar here rather than putting Shop-aware logic in the node factory.
    const hiddenSidebar = [
      VIEW_FROM_FILTER_NODE_KEY_V2,
      SYSTEM_COMPATIBILITY_FILTER_NODE_KEY_V2,
      FEATURE_CONFIG_BRAND_FILTER_NODE_KEY_V2,
    ]
    const overrideLabels = { [GLOBAL_SUPPLIER_NODE_KEY_V2]: 'Suppliers' }

    return HARDWARE_FILTERS_DESIGN_PAGE_CONFIG_V2.filter((config) => !hiddenSidebar.includes(config.key)).map(
      (config) => ({
        ...config,
        label: overrideLabels[config.key] || config.label,
      })
    )
  }, [])

  const ComponentTypes = useMemo(() => {
    if (
      (componentType && othersKeyWords.includes(componentType)) ||
      (searchText && othersKeyWords.includes(searchText))
    ) {
      return otherSubtypes && otherSubtypes.length > 0 ? otherSubtypes : otherCategories
    }

    // check whether the search term is a component type category
    const isMajorComponent = searchText && majorComponentCategories.includes(searchText)
    if (!componentType && searchText && isMajorComponent) {
      return [searchText]
    }

    return componentType ? [componentType] : componentType
  }, [componentType, otherSubtypes, searchText]) as ComponentTypesV2[]

  const pageSize = 20

  // Include search in defaults so the initial list request (before any effect runs) uses the URL search term.
  const defaultFilterValues = useMemo(() => {
    const defaults = {
      ...(searchText && !majorComponentCategories.includes(searchText) ? { search: searchText } : {}),
    }

    if (distributor) {
      const distributorSet = new Set([distributor])
      defaults[DISTRIBUTOR_FILTER_KEY_V2] = listQueryParamsFormatter.format({ value: distributorSet })
    }

    return defaults
  }, [distributor, searchText])

  const hasInitialData = initialComponentsData && initialComponentsData.length > 0

  const {
    componentsData: fetchedComponentsData,
    modifiableFilterValues,
    persistentFilterValues,
    allFilterValues,
    setFilters,
    setPage,
    total,
    page,
    loaded,
    loading: fetchLoading,
  } = useHardwareSelectorFetchEngine({
    componentTypes: ComponentTypes,
    limit: pageSize,
    sortBy: '_distributor_count',
    defaultFilterValues,
    persistentFilterValuesOverride: { referrer: 'shop' },
    blockRequestInitially: !!hasInitialData,
  })

  const componentsData = hasInitialData
    ? initialComponentsData.slice((localPage - 1) * pageSize, localPage * pageSize)
    : fetchedComponentsData
  const loading = hasInitialData ? false : fetchLoading
  const noResult = componentsData?.length === 0

  const activePage = hasInitialData ? localPage : page
  const activeTotal = hasInitialData ? initialComponentsData.length : total
  const activeSetPage = hasInitialData ? setLocalPage : setPage

  useEffect(() => {
    const globalFilters = {
      [DISTRIBUTOR_FILTER_KEY_V2]: modifiableFilterValues[DISTRIBUTOR_FILTER_KEY_V2],
      search: modifiableFilterValues.search,
    }

    Object.keys(globalFilters).forEach((key) => {
      if (globalFilters[key] === undefined) {
        delete globalFilters[key]
      }
    })

    setFilters(globalFilters)
  }, [componentType])

  useEffect(() => {
    const filters = {
      ...modifiableFilterValues,
      search: searchText,
    }

    const isMajorComponent = searchText && majorComponentCategories.includes(searchText)
    if (isMajorComponent) {
      delete filters['search']
    }

    setFilters(filters)
  }, [searchText])

  const portalContainer = document.getElementById(HARDWARE_FILTER_SIDEBAR_ID)

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
        <ShopPageContainer>
          <Container>
            <Portal container={portalContainer}>
              <HardwareSelectorFilterSidebar
                hardwareFilterConfigList={hardware_config_sidebar}
                persistentFilterValues={persistentFilterValues}
                modifiableFilterValues={modifiableFilterValues}
                allFilterValues={allFilterValues}
                setModifiableFilterValues={setFilters}
                componentTypes={ComponentTypes}
                location="shop"
              />
            </Portal>
            {!loading && noResult ? (
              <NoResults
                text={
                  searchText || translate(supplierName ? `${supplierName} ${componentTypeLabel}` : componentTypeLabel)
                }
              />
            ) : (
              <Grid container direction="column">
                <Grid item>
                  {initialTitle && <Typography textVariant="h5Bold">{initialTitle}</Typography>}
                  {!initialTitle && searchText && (
                    <Typography textVariant="h5Bold">
                      {supplierName
                        ? translate(`All ${supplierName} ${componentTypeLabel}`)
                        : translate(`All ${componentTypeLabel}`)}
                    </Typography>
                  )}
                  {!initialTitle && !searchText && (
                    <Typography textVariant="h5Bold">{` ${translate(`All`)} ${componentTypeLabel}`} </Typography>
                  )}
                  <Typography textVariant="body2" colorHex={theme.palette.neutralGrey[1000]}>
                    {loading
                      ? translate('Loading...')
                      : `${activeTotal} ${activeTotal === 1 ? translate('result') : translate('results')}`}
                  </Typography>
                </Grid>
                <Grid item>
                  <ShopPagination
                    page={activePage}
                    limit={pageSize}
                    total={activeTotal}
                    onNexPage={(nextPage) => activeSetPage(nextPage)}
                    onPreviousPage={(previousPage) => activeSetPage(previousPage)}
                    loading={loading}
                  />
                </Grid>
              </Grid>
            )}
            {!loading && noResult ? (
              <NoResultFoundPage text={translate('No results found')} />
            ) : (
              <ProductGrid
                componentsData={componentsData}
                carouselMode={false}
                loading={loading && !loaded}
                isFetching={loading}
                pageSize={pageSize}
              />
            )}
            <Grid item>
              <ShopPagination
                page={activePage}
                limit={pageSize}
                total={activeTotal}
                onNexPage={(nextPage) => activeSetPage(nextPage)}
                onPreviousPage={(previousPage) => activeSetPage(previousPage)}
                loading={loading}
              />
            </Grid>
          </Container>
        </ShopPageContainer>
      </DistributorTradePricingContext.Provider>
    </ComponentVersionsInherit>
  )
}

export default ShopItemsWithFilter
